// frontend/src/pages/booking/steps/ConfirmationStep.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../../services/api';

const ConfirmationStep = ({ bookingData }) => {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookingDetails();
  }, []);

  const loadBookingDetails = async () => {
    try {
      const response = await API.bookings.get(bookingData.reference);
      setBooking(response.data);
    } catch (err) {
      console.error('Failed to load booking details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintConfirmation = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
      {/* Success Animation */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-24 h-24 bg-emerald-600 rounded-full mb-6 animate-bounce">
          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h2 className="text-4xl font-playfair font-bold text-white mb-4">
          Booking Confirmed! 🎉
        </h2>
        <p className="text-xl text-gray-300 mb-2">
          Your adventure awaits!
        </p>
        <p className="text-gray-400">
          A confirmation email has been sent to {bookingData.customerInfo?.email || booking?.customer_email}
        </p>
      </div>

      {/* Booking Reference */}
      <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-xl p-6 mb-8 text-center">
        <p className="text-emerald-100 text-sm mb-2">Booking Reference</p>
        <p className="text-3xl font-bold text-white mb-3 tracking-wider">
          {bookingData.reference || booking?.booking_reference}
        </p>
        <p className="text-emerald-100 text-sm">
          Keep this reference number for your records
        </p>
      </div>

      {/* Booking Details */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-700 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Tour Details
          </h3>
          <div className="space-y-3 text-gray-300">
            <div className="flex justify-between">
              <span>Tour:</span>
              <span className="font-bold text-white">{booking?.tour_name || bookingData.tour?.name}</span>
            </div>
            <div className="flex justify-between">
              <span>Date:</span>
              <span className="font-bold text-white">{booking?.formatted_date || bookingData.selectedDate}</span>
            </div>
            <div className="flex justify-between">
              <span>Time:</span>
              <span className="font-bold text-white">{booking?.formatted_time || bookingData.selectedTime}</span>
            </div>
            <div className="flex justify-between">
              <span>Duration:</span>
              <span className="font-bold text-white">{bookingData.tour?.duration}</span>
            </div>
            <div className="flex justify-between">
              <span>Participants:</span>
              <span className="font-bold text-white">{booking?.num_participants || bookingData.participants?.length}</span>
            </div>
            {/* ✅ ADD: ATV Breakdown for Mixed Bookings */}
            {(bookingData.rideType === 'mixed' || booking?.atv_type === 'mixed') && (
              <div className="flex justify-between">
                <span>ATV Details:</span>
                <span className="font-bold text-white">
                  {bookingData.numSingleAtvs || booking?.num_single_atvs || 0} Single + {' '}
                  {bookingData.numDoubleAtvs || booking?.num_double_atvs || 0} Double
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-700 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Contact Details
          </h3>
          <div className="space-y-3 text-gray-300">
            <div className="flex justify-between">
              <span>Name:</span>
              <span className="font-bold text-white">{booking?.customer_name || bookingData.customerInfo?.name}</span>
            </div>
            <div className="flex justify-between">
              <span>Email:</span>
              <span className="font-bold text-white text-sm">{booking?.customer_email || bookingData.customerInfo?.email}</span>
            </div>
            <div className="flex justify-between">
              <span>Phone:</span>
              <span className="font-bold text-white">{booking?.customer_phone || bookingData.customerInfo?.phone}</span>
            </div>
            <div className="flex justify-between">
              <span>Pickup:</span>
              <span className="font-bold text-white text-sm">{booking?.pickup_location || bookingData.customerInfo?.pickup_location}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Summary */}
      <div className="bg-gray-700 rounded-xl p-6 mb-8">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          Payment Summary
        </h3>
        <div className="space-y-2 text-gray-300">
          <div className="flex justify-between">
            <span>Amount Paid:</span>
            <span className="font-bold text-white">${booking?.total_amount || bookingData.totalAmount}</span>
          </div>
          <div className="flex justify-between">
            <span>Payment Status:</span>
            <span className="px-3 py-1 bg-emerald-600 text-white rounded-full text-sm font-bold">
              {booking?.payment_status === 'paid' ? 'Paid' : 'Confirmed'}
            </span>
          </div>
        </div>
      </div>

      {/* What's Next */}
      <div className="bg-gradient-to-br from-gray-700 to-gray-600 rounded-xl p-6 mb-8">
        <h3 className="text-xl font-bold text-white mb-4">What's Next?</h3>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
              1
            </div>
            <div>
              <h4 className="font-bold text-white mb-1">Check Your Email</h4>
              <p className="text-gray-300 text-sm">
                We've sent a confirmation email with all tour details and a waiver copy.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
              2
            </div>
            <div>
              <h4 className="font-bold text-white mb-1">Prepare for Your Tour</h4>
              <p className="text-gray-300 text-sm">
                Wear comfortable clothing, closed-toe shoes, and bring sunscreen and water.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
              3
            </div>
            <div>
              <h4 className="font-bold text-white mb-1">Arrive 15 Minutes Early</h4>
              <p className="text-gray-300 text-sm">
                Please arrive at your pickup location 15 minutes before the scheduled time.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* <button
          onClick={handlePrintConfirmation}
          className="flex-1 px-8 py-4 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-3"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print Confirmation
        </button> */}

        <button
          onClick={() => navigate('/tours')}
          className="flex-1 px-8 py-4 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-3"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          Back to Home
        </button>
      </div>

      {/* Contact Support */}
      <div className="mt-8 text-center text-gray-400 text-sm">
        <p>Need help? Contact us at</p>
        <a href="mailto:info@layouadventures.com" className="text-emerald-400 hover:text-emerald-300 font-bold">
          info@layouadventures.com
        </a>
        <span className="mx-2">or call</span>
        <a href="tel:+16143852846" className="text-emerald-400 hover:text-emerald-300 font-bold">
          (614) 385-2846
        </a>
      </div>
    </div>
  );
};

export default ConfirmationStep;